import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    reorderUrl: String,
  }

  connect() {
    this.dragged = null
    this.currentTarget = null
    this.bindDragEvents()
  }

  bindDragEvents() {
    this.items().forEach((item) => {
      const card = item.querySelector(".dashboard-widget-card")
      const targets = [item, card].filter(Boolean)
      targets.forEach((target) => {
        target.setAttribute("draggable", "true")
        target.addEventListener("dragstart", this.onDragStart)
        target.addEventListener("dragover", this.onDragOver)
        target.addEventListener("drop", this.onDrop)
        target.addEventListener("dragend", this.onDragEnd)
      })
    })
  }

  onDragStart = (event) => {
    const item = event.currentTarget.closest("[data-widget-id]") || event.currentTarget
    this.dragged = item
    event.dataTransfer.effectAllowed = "move"
    event.currentTarget.classList.add("is-dragging")
    this.element.classList.add("is-dragging-active")
    this.items().forEach((item) => {
      item.classList.add("is-drop-zone")
      const card = item.querySelector(".dashboard-widget-card")
      if (card) card.classList.add("is-drop-zone")
      this.applyDropStyles(item, card, false)
    })
    this.hideDropBadges()
  }

  onDragOver = (event) => {
    event.preventDefault()
    const hovered = document.elementFromPoint(event.clientX, event.clientY)
    const targetItem = hovered?.closest?.("[data-widget-id]") || event.target.closest?.("[data-widget-id]") || event.currentTarget.closest("[data-widget-id]")
    if (!this.dragged || !targetItem || targetItem === this.dragged) return
    if (this.currentTarget === targetItem) return
    this.currentTarget = targetItem
    this.items().forEach((item) => {
      item.classList.remove("is-drop-target")
      const card = item.querySelector(".dashboard-widget-card")
      if (card) card.classList.remove("is-drop-target")
      this.applyDropStyles(item, card, false)
    })
    targetItem.classList.add("is-drop-target")
    const targetCard = targetItem.querySelector(".dashboard-widget-card")
    if (targetCard) targetCard.classList.add("is-drop-target")
    this.applyDropStyles(targetItem, targetCard, true)
    this.showDropBadge(targetCard)
    const rect = targetItem.getBoundingClientRect()
    this.dropAfter = event.clientY - rect.top > rect.height / 2
  }

  onDrop = (event) => {
    event.preventDefault()
    if (this.dragged && this.currentTarget) {
      const parent = this.currentTarget.parentNode
      let referenceNode = this.dropAfter ? this.currentTarget.nextElementSibling : this.currentTarget
      if (referenceNode === this.dragged) {
        referenceNode = referenceNode.nextElementSibling
      }
      parent.insertBefore(this.dragged, referenceNode || null)
    }
    this.persistOrder()
  }

  onDragEnd = (event) => {
    event.currentTarget.classList.remove("is-dragging")
    this.element.classList.remove("is-dragging-active")
    this.currentTarget = null
    this.dropAfter = null
    this.items().forEach((item) => {
      item.classList.remove("is-drop-zone", "is-drop-target")
      const card = item.querySelector(".dashboard-widget-card")
      if (card) card.classList.remove("is-drop-zone", "is-drop-target")
      this.clearDropStyles(item, card)
    })
    this.hideDropBadges()
    this.persistOrder()
  }

  persistOrder() {
    if (!this.hasReorderUrlValue) return
    const ids = this.items().map((item) => item.dataset.widgetId).filter(Boolean)
    if (ids.length === 0) return
    fetch(this.reorderUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ ordered_ids: ids }),
    })
  }

  items() {
    return Array.from(this.element.querySelectorAll("[data-widget-id]"))
  }

  applyDropStyles(item, card, isTarget) {
    const border = isTarget ? "2px dashed rgba(15, 118, 110, 1)" : "1px dashed rgba(15, 118, 110, 0.35)"
    const bg = isTarget ? "rgba(15, 118, 110, 0.12)" : "rgba(15, 118, 110, 0.03)"
    const opacity = isTarget ? "1" : "0.65"
    if (item) {
      item.style.border = border
      item.style.borderRadius = "16px"
      item.style.backgroundColor = bg
      item.style.opacity = opacity
    }
    if (card) {
      card.style.border = border
      card.style.backgroundColor = bg
      card.style.position = "relative"
    }
  }

  clearDropStyles(item, card) {
    if (item) {
      item.style.border = ""
      item.style.borderRadius = ""
      item.style.backgroundColor = ""
      item.style.opacity = ""
    }
    if (card) {
      card.style.border = ""
      card.style.backgroundColor = ""
      card.style.position = ""
    }
  }

  showDropBadge(card) {
    if (!card) return
    this.hideDropBadges()
    const badge = document.createElement("span")
    badge.className = "dashboard-drop-badge"
    badge.textContent = "Drop here"
    card.appendChild(badge)
  }

  hideDropBadges() {
    this.element.querySelectorAll(".dashboard-drop-badge").forEach((badge) => badge.remove())
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.content : ""
  }
}
